import * as React from "react";
import { View, ViewStyle, StyleSheet, StyleProp } from "react-native";

type Props = React.ComponentPropsWithRef<typeof View> & {
    /**
     * Content of the `DialogScrollArea`.
     */
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

/**
 * A component to show a scrollable content in a Dialog. The component only provides appropriate styling.
 * For the scrollable content you can use `ScrollView`, `FlatList` etc. depending on your requirement.
 *
 * <div class="screenshots">
 *   <figure>
 *     <img class="medium" src="screenshots/dialog-scroll-area.gif" />
 *   </figure>
 * </div>
 *
 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { ScrollView } from 'react-native';
 * import Text from 'react-native-simple-elements/components/Text';
 * import Dialog from 'react-native-simple-elements/components/Dialog';
 * import Portal from 'react-native-simple-elements/components/Portal';
 *
 * const MyComponent = () => {
 *   const [visible, setVisible] = React.useState(false);
 *
 *   const hideDialog = () => setVisible(false);
 *
 *   return (
 *     <Portal>
 *       <Dialog visible={visible} onDismiss={hideDialog}>
 *         <Dialog.ScrollArea>
 *           <ScrollView contentContainerStyle={{paddingHorizontal: 24}}>
 *             <Text>This is a scrollable area</Text>
 *           </ScrollView>
 *         </Dialog.ScrollArea>
 *       </Dialog>
 *     </Portal>
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 */
const DialogScrollArea = (props: Props) => (
    <View {...props} style={[styles.container, props.style]}>
        {props.children}
    </View>
);

DialogScrollArea.displayName = "Dialog.ScrollArea";

const styles = StyleSheet.create({
    container: {
        borderColor: "rgba(0, 0, 0, .12)",
        borderTopWidth: StyleSheet.hairlineWidth,
        borderBottomWidth: StyleSheet.hairlineWidth,
        paddingHorizontal: 24,
        flexGrow: 1,
        flexShrink: 1,
    },
});

export default DialogScrollArea;
